import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../../store';
import { updateIntegerState } from '../../../reducers/configurationReducer';

interface IntConfigProps {
  state: any;
  onValidationChange?: (hasError: boolean) => void;
}

const isEmpty = (v: any) =>
  v === undefined || v === null || v === "" || (typeof v === "number" && Number.isNaN(v));

const IntConfig = ({ state, onValidationChange }: IntConfigProps) => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string>("");

  // Notify parent: for map_cluster_radius only, also treat empty value as error so Save is disabled
  useEffect(() => {
    if (onValidationChange) {
      const emptyInvalid = state?.key === 'map_cluster_radius' && isEmpty(state?.value);
      onValidationChange(!!error || emptyInvalid);
    }
  }, [error, state?.value, state?.key, onValidationChange]);

  const handleChange = (e: any) => {
    const raw = e.target.value;
    if (raw === "") {
      dispatch(updateIntegerState(null as any));
      return;
    }
    const num = parseInt(raw, 10);
    dispatch(updateIntegerState(Number.isNaN(num) ? null : num));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      if (state?.key === 'map_cluster_radius') {
        setError("This field is required");
      } else {
        setError("");
      }
      handleChange(e);
      return;
    }

    const value = parseInt(inputValue);
    if (!isNaN(value)) {
      // Always update the state with user's input first
      handleChange(e);
      
      // Then check validation and set error if needed
      if (state?.key === 'appointment_expired_time' && value > 48) {
        setError("Number should be less than or equal to 48 hours");
      } else if (state?.key === 'time_to_live') {
        if (value < 1) {
          setError("Time to live should be at least 1 hours");
        } else if (value > 48) {
          setError("Time to live should not exceed 48 hours");
        } else {
          setError("");
        }
      } else if (state?.key === 'chat_font_size') {
        if (value < 14) {
          setError("Font size should be at least 14");
        } else if (value > 28) {
          setError("Font size should not exceed 28");
        } else {
          setError("");
        }
      } else if (state?.key === 'map_cluster_radius') {
        if (value < 0) {
          setError("Radius should be at least 0");
        } else if (value > 100) {
          setError("Radius should not exceed 100");
        } else {
          setError("");
        }
      } else {
        setError("");
        handleChange(e);
      }
    }
  };

  const getPlaceholder = () => {
    switch (state?.key) {
      case 'appointment_expired_time':
        return 'Hours';
      case 'time_to_live':
        return 'Hours (1-48)';
      case 'chat_font_size':
        return 'Size (14-28)';
      case 'map_cluster_radius':
        return 'Radius (0-100)';
      default:
        return 'Time';
    }
  };

  const getMinMaxValues = () => {
    switch (state?.key) {
      case 'appointment_expired_time':
        return { min: 1, max: 48 };
      case 'time_to_live':
        return { min: 1, max: 48 };
      case 'chat_font_size':
        return { min: 14, max: 28 };
      case 'map_cluster_radius':
        return { min: 0, max: 100 };
      default:
        return { min: 1, max: undefined };
    }
  };

  const { min, max } = getMinMaxValues();

  return (
    <div className='row'>
      <div className='col'>
        <input
          type='number'
          min={min}
          max={max}
          value={state?.value ?? ""}
          onChange={handleInputChange}
          className='form-control form-control-lg form-control-solid'
          placeholder={getPlaceholder()}
        />
        {error && (
          <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '5px' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntConfig;
